import React, { useEffect, useState } from "react";
import {
  SettingsContext,
  DEFAULT_SETTINGS,
  type AppSettings,
  type DisplaySettings,
} from "~/contexts/settingsContext";

const STORAGE_KEY = "divide_ttrpg_settings";

/**
 * SettingsProvider wraps the app and manages settings state.
 * Persists settings to localStorage as "divide_ttrpg_settings".
 */
export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [settings, setSettings] = useState<AppSettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<AppSettings>;
        // Merge with defaults to handle new settings
        return {
          ...DEFAULT_SETTINGS,
          display: {
            ...DEFAULT_SETTINGS.display,
            ...parsed.display,
          },
        };
      }
    } catch {
      // Invalid JSON, use defaults
    }
    return DEFAULT_SETTINGS;
  });

  // Persist to localStorage when settings change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const updateDisplaySettings = (updates: Partial<DisplaySettings>) => {
    setSettings((prev) => ({
      ...prev,
      display: {
        ...prev.display,
        ...updates,
      },
    }));
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  return (
    <SettingsContext.Provider
      value={{ settings, updateDisplaySettings, resetSettings }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
